import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from '../api/client';

export type Role = 'admin' | 'manager' | 'bayi';

export interface User {
  id: number;
  role: Role;
  email: string;
  full_name: string | null;
  phone: string | null;
  company_name: string | null;
  tax_number: string | null;
  address: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface RegisterData {
  email: string;
  password: string;
  fullName: string;
  companyName: string;
  taxNumber?: string;
  phone?: string;
  address?: string;
}

interface AuthCtx {
  user: User | null;
  loading: boolean;
  /** Admin, bir bayi hesabına geçici giriş yaptıysa true. */
  impersonating: boolean;
  /** Şifre doğru → admin/manager için 2FA gerekiyorsa {twofa:true}; aksi halde girişi tamamlar. */
  login: (email: string, password: string) => Promise<{ twofa: true } | { twofa: false; user: User }>;
  /** 2FA kodunu doğrular; girişi tamamlar. remember=true ise cihaz 30 gün hatırlanır. */
  verify2fa: (code: string, remember: boolean) => Promise<User>;
  /** Bayi self-servis kaydı — hesap PASİF oluşturulur (yönetici onayına kadar giriş yok). */
  register: (data: RegisterData) => Promise<{ pending: true }>;
  logout: () => Promise<void>;
  /** Admin: bayi hesabına geçici giriş. */
  impersonate: (dealerId: number) => Promise<User>;
  /** Geçici girişten çıkıp asıl admin hesabına dön. */
  stopImpersonation: () => Promise<User>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [impersonating, setImpersonating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ user: User; impersonating?: boolean }>('/auth/me')
      .then((r) => {
        setUser(r.user);
        setImpersonating(Boolean(r.impersonating));
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    const r = await api.post<{ user?: User; twofa?: boolean }>('/auth/login', { email, password });
    if (r.twofa) return { twofa: true as const };
    setUser(r.user!);
    setImpersonating(false);
    return { twofa: false as const, user: r.user! };
  };

  const verify2fa = async (code: string, remember: boolean) => {
    const r = await api.post<{ user: User }>('/auth/2fa', { code, remember });
    setUser(r.user);
    setImpersonating(false);
    return r.user;
  };

  const register = async (data: RegisterData) => {
    // Hesap pasif oluşturulur; oturum açılmaz. Yönetici onayı sonrası giriş yapılır.
    await api.post<{ pending: true }>('/auth/register', data);
    return { pending: true as const };
  };

  const logout = async () => {
    await api.post('/auth/logout');
    setUser(null);
    setImpersonating(false);
  };

  const impersonate = async (dealerId: number) => {
    const r = await api.post<{ user: User }>(`/admin/dealers/${dealerId}/impersonate`);
    setUser(r.user);
    setImpersonating(true);
    return r.user;
  };

  const stopImpersonation = async () => {
    const r = await api.post<{ user: User }>('/auth/stop-impersonation');
    setUser(r.user);
    setImpersonating(false);
    return r.user;
  };

  return (
    <Ctx.Provider
      value={{ user, loading, impersonating, login, verify2fa, register, logout, impersonate, stopImpersonation }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth, AuthProvider içinde kullanılmalı');
  return ctx;
}
